import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { Translations } from '../types/i18n';
import { isCapacitorApp, normalizeLanguageCode } from '../utils/platform';

// Import all locale files
import en from './locales/en.json';
import es from './locales/es.json';
import he from './locales/he.json';
import yi from './locales/yi.json';

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation';
    resources: {
      en: Translations;
      es: Translations;
      he: Translations;
      yi: Translations;
    };
  }
}

export interface LanguageConfig {
  code: string;
  name: string;
  dir: 'ltr' | 'rtl';
  flag: string;
}

export const languageConfigs: { [key: string]: LanguageConfig } = {
  en: { code: 'en', name: 'English', dir: 'ltr', flag: '🇺🇸' },
  es: { code: 'es', name: 'Español', dir: 'ltr', flag: '🇪🇸' },
  he: { code: 'he', name: 'עברית', dir: 'rtl', flag: '🇮🇱' },
  yi: { code: 'yi', name: 'ייִדיש', dir: 'rtl', flag: '🇮🇱' },
};

const resources = {
  en: { translation: en },
  es: { translation: es },
  he: { translation: he },
  yi: { translation: yi },
} as const;

export const defaultNS = 'translation';
export const supportedLngs = Object.keys(languageConfigs);

// Function to set document language and direction
const setDocumentLanguage = (language: string) => {
  const normalizedLng = normalizeLanguageCode(language);
  document.documentElement.lang = normalizedLng;
  document.documentElement.dir = ['he', 'yi'].includes(normalizedLng) ? 'rtl' : 'ltr';
  
  // Add a specific class for Yiddish to help with font targeting
  if (normalizedLng === 'yi') {
    document.documentElement.classList.add('lang-yiddish');
  } else {
    document.documentElement.classList.remove('lang-yiddish');
  }
};

// Get detection order based on platform
const getDetectionOrder = () => {
  // For mobile apps, prioritize localStorage over navigator
  // This is because navigator.language might not be reliable in WebViews
  if (isCapacitorApp()) {
    return ['localStorage', 'htmlTag', 'path', 'subdomain'];
  }
  
  // For web, use the default order
  return ['navigator', 'localStorage', 'htmlTag', 'path', 'subdomain'];
};

// Override console methods to filter out i18next-related messages
const originalWarn = console.warn;
const originalError = console.error;
const originalLog = console.log;

// Filter function to check if a message is i18next-related or test-related
const isFilteredMessage = (args: any[]): boolean => {
  if (args.length === 0) return false;
  
  const firstArg = args[0];
  
  // Handle non-string arguments
  if (typeof firstArg !== 'string') {
    // Check if it's an object with a message property that might contain filtered terms
    if (firstArg && typeof firstArg === 'object' && firstArg.message && typeof firstArg.message === 'string') {
      const message = firstArg.message.toLowerCase();
      return message.includes('i18next') || 
             message.includes('loki') || 
             message.includes('test') ||
             message.includes('storybook');
    }
    return false;
  }
  
  const lowerArg = firstArg.toLowerCase();
  
  // i18next-related messages
  const isI18nextMessage = 
    lowerArg.includes('i18next::') ||
    lowerArg.includes('key not found:') ||
    lowerArg.includes('missingkey') ||
    lowerArg.includes('i18next:') ||
    lowerArg.includes('translation not found');
  
  // Loki and test-related messages
  const isTestMessage = 
    lowerArg.includes('loki:') ||
    lowerArg.includes('[loki]') ||
    lowerArg.includes('test:') ||
    lowerArg.includes('[test]') ||
    lowerArg.includes('storystore:') ||
    lowerArg.includes('[storybook]') ||
    lowerArg.includes('jest') ||
    lowerArg.includes('cypress') ||
    lowerArg.includes('playwright') ||
    lowerArg.includes('selenium');
  
  // General logging framework messages
  const isLoggingMessage =
    lowerArg.includes('[log]') ||
    lowerArg.includes('logger:') ||
    lowerArg.includes('[debug]') ||
    lowerArg.includes('[trace]') ||
    lowerArg.includes('[verbose]');
  
  return isI18nextMessage || isTestMessage || isLoggingMessage;
};

// Override console.warn
console.warn = function(...args: any[]) {
  if (isFilteredMessage(args)) return;
  originalWarn.apply(console, args);
};

// Override console.error
console.error = function(...args: any[]) {
  if (isFilteredMessage(args)) return;
  originalError.apply(console, args);
};

// Override console.log for i18next-specific logs
console.log = function(...args: any[]) {
  if (isFilteredMessage(args)) return;
  
  // Still log language changes but in a cleaner format
  if (args.length > 0 && typeof args[0] === 'string' && 
      (args[0].includes('Language changed to:') || 
       args[0].includes('Initial language:'))) {
    // Allow these but don't duplicate them
    if (!args[0].includes('Normalized language:')) {
      originalLog.apply(console, args);
    }
    return;
  }
  
  // Remove "Using web detection order" log
  if (args.length > 0 && typeof args[0] === 'string' && 
      args[0].includes('Using web detection order')) {
    return;
  }
  
  originalLog.apply(console, args);
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    defaultNS,
    supportedLngs,
    resources,
    fallbackLng: 'en',
    debug: false, // Set to false to reduce console noise

    interpolation: {
      escapeValue: false,
    },

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    detection: {
      order: getDetectionOrder(),
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },

    react: {
      useSuspense: true,
    },
  });

// Add language change listener to log language changes
i18n.on('languageChanged', (lng) => {
  // Store the normalized language code in localStorage
  const normalizedLng = normalizeLanguageCode(lng);
  localStorage.setItem('i18nextLng', normalizedLng);
  
  // Set document language and direction
  setDocumentLanguage(lng);
});

// Set initial language
setDocumentLanguage(i18n.language);

export default i18n; 